import { useCallback } from 'react';

import type {
  GeneralInfoModalProps,
  GeneralPromptModalProps,
  ModalType,
} from '../../components/layouts/modal/modals';
import { Actions } from '../../constants/actionTypes';
import { useMainDispatch } from '../useMainDispatch';
import { useMainSelector } from '../useMainSelector';

interface BaseModalParams {
  title: string;
}

type PromptModalParams = BaseModalParams & GeneralPromptModalProps;

type InfoModalParams = BaseModalParams & GeneralInfoModalProps;

export const useModal = () => {
  const {
    ui: { activeModal },
  } = useMainSelector();
  const dispatch = useMainDispatch();

  const showModal = useCallback(
    (type: ModalType, title?: string, props?: any) => {
      dispatch({
        type: Actions.SET_UI,
        payload: {
          activeModal: {
            type,
            title,
            props,
          },
        },
      });
    },
    [dispatch],
  );

  const hideModal = useCallback(() => {
    dispatch({ type: Actions.SET_UI, payload: { activeModal: undefined } });
  }, [dispatch]);

  const showPromptModal = useCallback(
    ({
      title,
      description,
      onSubmit,
      onAbort,
      type,
      customButtons,
      shouldAutomaticallyClose,
    }: PromptModalParams) => {
      showModal('general-prompt-modal', title, {
        description,
        type,
        onSubmit,
        onAbort,
        customButtons,
        shouldAutomaticallyClose,
      });
    },
    [showModal],
  );

  const showInfoModal = useCallback(
    ({ title, text, options }: InfoModalParams) => {
      showModal('general-info-modal', title, { text, options });
    },
    [showModal],
  );

  return {
    showInfoModal,
    hideModal,
    type: activeModal?.type,
    title: activeModal?.title,
    props: activeModal?.props,
    showModal,
    showPromptModal,
  };
};
